import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { inferTeachingRole } from '../../utils/teachingRole';

export type WorkspaceMode = 'student' | 'class';
export type StudentStatus = 'ok' | 'due' | 'critical';

export type WorkspaceScope =
  | { scope: 'student'; studentId: string }
  | { scope: 'class'; classId: string }
  | { scope: 'none' };

export type WorkspaceStudent = {
  id: string;
  label: string;
  className: string;
  status: StudentStatus;
};

export type WorkspaceClass = {
  className: string;
};

export type TimelineType = 'today' | 'due' | 'pending' | 'suggested';
export type TimelineItem = {
  id: string;
  type: TimelineType;
  title: string;
  subtitle: string;
  studentId?: string | null;
  actionLabel: string;
  actionHref: string;
};

export type AiSuggestion = {
  id: string;
  title: string;
  subtitle: string;
  actionHref: string;
};

export type WorkspaceState = {
  mode: WorkspaceMode;
  activeStudentId: string | null;
  activeClassId: string | null;
  lastStudentId: string | null;
  lastClassId: string | null;
  students: WorkspaceStudent[];
  studentsLoading: boolean;
  studentsError: string | null;
  studentsReloadToken: number;
  classes: WorkspaceClass[];
  classesLoading: boolean;
  classesError: string | null;
  classesReloadToken: number;
  classContext: { className: string };
  timelineItems: TimelineItem[];
  aiSuggestions: AiSuggestion[];
  quickClassTools: Array<{ id: string; title: string; icon: string; href: string }>;

  activeTeachingRole: 'curricolare' | 'sostegno' | null;

  getScope: () => WorkspaceScope;

  setMode: (mode: WorkspaceMode) => void;
  setActiveStudentId: (id: string | null) => void;
  setActiveClassId: (id: string | null) => void;

  // “real mode” helpers
  setActiveStudent: (id: string | null) => void;
  setActiveClass: (id: string | null, className?: string) => void;
  setModeStudent: () => void;
  setModeClass: () => void;
  clearContext: () => void;
  setStudents: (students: WorkspaceStudent[]) => void;
  setStudentsLoading: (v: boolean) => void;
  setStudentsError: (message: string | null) => void;
  requestReloadStudents: () => void;
  setClasses: (classes: WorkspaceClass[]) => void;
  setClassesLoading: (v: boolean) => void;
  setClassesError: (message: string | null) => void;
  requestReloadClasses: () => void;
  setClassName: (className: string) => void;
  setTimelineItems: (items: TimelineItem[]) => void;
};

const LS_MODE_KEY = 'sp_ws_mode_v1';
const LS_ACTIVE_STUDENT_KEY = 'sp_ws_active_student_v1';
const LS_ACTIVE_CLASS_KEY = 'sp_ws_active_class_v1';
const LS_LAST_STUDENT_KEY = 'sp_ws_last_student_v1';
const LS_LAST_CLASS_KEY = 'sp_ws_last_class_v1';
const LS_CLASS_NAME_KEY = 'sp_ws_class_name_v1';

function safeReadString(key: string, fallback: string | null = null): string | null {
  try {
    const v = localStorage.getItem(key);
    if (v === null || v === undefined) return fallback;
    const t = String(v).trim();
    return t ? t : fallback;
  } catch {
    return fallback;
  }
}

function safeWriteString(key: string, value: string | null) {
  try {
    if (!value) localStorage.removeItem(key);
    else localStorage.setItem(key, String(value));
  } catch {
    // ignore
  }
}

const WorkspaceContext = createContext<WorkspaceState | null>(null);

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within WorkspaceProvider');
  return ctx;
}

export function WorkspaceProvider(props: { children: React.ReactNode }) {
  const { userProfile } = useAuth() || ({} as any);

  const initialMode = (safeReadString(LS_MODE_KEY, 'student') as WorkspaceMode) || 'student';
  const [mode, setMode] = useState<WorkspaceMode>(initialMode === 'class' ? 'class' : 'student');

  const [activeStudentId, setActiveStudentId] = useState<string | null>(safeReadString(LS_ACTIVE_STUDENT_KEY, null));
  const [activeClassId, setActiveClassId] = useState<string | null>(safeReadString(LS_ACTIVE_CLASS_KEY, null));
  const [lastStudentId, setLastStudentId] = useState<string | null>(safeReadString(LS_LAST_STUDENT_KEY, null));
  const [lastClassId, setLastClassId] = useState<string | null>(safeReadString(LS_LAST_CLASS_KEY, null));
  const [students, setStudents] = useState<WorkspaceStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState<string | null>(null);
  const [studentsReloadToken, setStudentsReloadToken] = useState(0);

  const [classes, setClasses] = useState<WorkspaceClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [classesReloadToken, setClassesReloadToken] = useState(0);

  const [classContext, setClassContext] = useState<{ className: string }>({ className: safeReadString(LS_CLASS_NAME_KEY, '—') || '—' });
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);

  const setClassName = useCallback((className: string) => setClassContext({ className }), []);

  // persist key workspace fields
  useEffect(() => {
    safeWriteString(LS_MODE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    safeWriteString(LS_ACTIVE_STUDENT_KEY, activeStudentId);
  }, [activeStudentId]);

  useEffect(() => {
    safeWriteString(LS_ACTIVE_CLASS_KEY, activeClassId);
  }, [activeClassId]);

  useEffect(() => {
    safeWriteString(LS_LAST_STUDENT_KEY, lastStudentId);
  }, [lastStudentId]);

  useEffect(() => {
    safeWriteString(LS_LAST_CLASS_KEY, lastClassId);
  }, [lastClassId]);

  useEffect(() => {
    safeWriteString(LS_CLASS_NAME_KEY, classContext.className);
  }, [classContext.className]);

  // validate selected ids whenever students list changes
  useEffect(() => {
    if (!students.length) {
      // keep persisted values; UI will ask user to pick
      return;
    }

    if (activeStudentId && !students.some((s) => s.id === activeStudentId)) {
      setActiveStudentId(null);
    }
  }, [students, classes, mode, activeStudentId, activeClassId]);

  const getScope = useCallback((): WorkspaceScope => {
    if (mode === 'student' && activeStudentId) return { scope: 'student', studentId: activeStudentId };
    if (mode === 'class' && activeClassId) return { scope: 'class', classId: activeClassId };
    return { scope: 'none' };
  }, [mode, activeStudentId, activeClassId]);

  const setActiveStudent = useCallback((id: string | null) => {
    setMode('student');
    setActiveStudentId(id);
    if (id) setActiveClassId(null);
    if (id) setLastStudentId(id);
  }, []);

  const setActiveClass = useCallback((id: string | null, className?: string) => {
    setMode('class');
    setActiveClassId(id);
    setActiveStudentId(null);
    if (className) setClassContext({ className });
    else if (id) setClassContext({ className: id });
    if (id) setLastClassId(id);
  }, []);

  const setModeStudent = useCallback(() => {
    setMode('student');
    setActiveClassId(null);
    if (!activeStudentId && lastStudentId) setActiveStudentId(lastStudentId);
  }, [activeStudentId, lastStudentId]);

  const setModeClass = useCallback(() => {
    setMode('class');
    setActiveStudentId(null);
    if (!activeClassId && lastClassId) {
      setActiveClassId(lastClassId);
      setClassContext({ className: lastClassId });
    }
  }, [activeClassId, lastClassId]);

  // Enforce workspace mode from teaching role (registration/settings).
  // This hides the concept of a manual mode switch from the UI.
  const roleHint = useMemo(() => {
    const v = String(
      userProfile?.teaching_role ||
        userProfile?.teachingRole ||
        userProfile?.role ||
        userProfile?.ruolo ||
        ''
    )
      .trim()
      .toLowerCase();
    return v ? v : null;
  }, [userProfile]);

  const enforcedMode: WorkspaceMode | null = useMemo(() => {
    if (!roleHint) return null;
    const r = inferTeachingRole(userProfile);
    return r === 'curricolare' ? 'class' : 'student';
  }, [roleHint, userProfile]);

  const activeTeachingRole = useMemo<'curricolare' | 'sostegno' | null>(() => {
    if (!roleHint) return null;
    const r = inferTeachingRole(userProfile);
    return r === 'curricolare' ? 'curricolare' : 'sostegno';
  }, [roleHint, userProfile]);

  // Enforce workspace mode from teaching role.
  // IMPORTANT: set mode directly — do NOT call setModeClass/setModeStudent here
  // because those try to restore lastStudentId/lastClassId which can loop
  // if the stored ID doesn't match any loaded entity.
  useEffect(() => {
    if (!enforcedMode) return;
    if (enforcedMode !== mode) setMode(enforcedMode);
  }, [enforcedMode, mode]);

  const clearContext = useCallback(() => {
    setActiveStudentId(null);
    setActiveClassId(null);
  }, []);

  const requestReloadStudents = useCallback(() => {
    setStudentsReloadToken((x) => x + 1);
  }, []);

  const requestReloadClasses = useCallback(() => {
    setClassesReloadToken((x) => x + 1);
  }, []);

  const aiSuggestions = useMemo<AiSuggestion[]>(
    () => [
      {
        id: 's1',
        title: 'Prepara una verifica facilitata',
        subtitle: 'Numeri • 3 livelli • soluzioni',
        actionHref: '/ai-didattica?cmd=' + encodeURIComponent('Crea una verifica facilitata sui numeri: 10 domande, 3 livelli, soluzioni.')
      },
      {
        id: 's2',
        title: 'Crea una scheda semplificata',
        subtitle: 'Lettura • comprensione • soluzioni',
        actionHref:
          '/ai-didattica?cmd=' +
          encodeURIComponent('Crea una scheda semplificata per lettura: frasi brevi, punti elenco, comprensione con domande e soluzioni.')
      },
      {
        id: 's3',
        title: 'Imposta una routine di focus',
        subtitle: '5 minuti • 3 esercizi',
        actionHref: '/ai-didattica?cmd=' + encodeURIComponent('Prepara una routine breve e 3 attività per allenare attenzione e focus.')
      }
    ],
    []
  );

  const quickClassTools = useMemo(
    () => [
      { id: 't1', title: 'Timer + Routine', icon: 'timer', href: '/calendario' },
      { id: 't2', title: 'Quiz Live', icon: 'quiz', href: '/quiz-live' },
      { id: 't3', title: 'CAA', icon: 'caa', href: '/caa' },
      { id: 't4', title: 'Note veloci', icon: 'note', href: '/diario' }
    ],
    []
  );

  const value = useMemo<WorkspaceState>(
    () => ({
      mode,
      activeStudentId,
      activeClassId,
      lastStudentId,
      lastClassId,
      students,
      studentsLoading,
      studentsError,
      studentsReloadToken,
      classes,
      classesLoading,
      classesError,
      classesReloadToken,
      classContext,
      timelineItems,
      aiSuggestions,
      quickClassTools,
      activeTeachingRole,
      getScope,
      setMode,
      setActiveStudentId,
      setActiveClassId,
      setActiveStudent,
      setActiveClass,
      setModeStudent,
      setModeClass,
      clearContext,
      setStudents,
      setStudentsLoading,
      setStudentsError,
      requestReloadStudents,
      setClasses,
      setClassesLoading,
      setClassesError,
      requestReloadClasses,
      setClassName,
      setTimelineItems
    }),
    [
      mode,
      activeStudentId,
      activeClassId,
      lastStudentId,
      lastClassId,
      students,
      studentsLoading,
      studentsError,
      studentsReloadToken,
      classes,
      classesLoading,
      classesError,
      classesReloadToken,
      classContext,
      timelineItems,
      aiSuggestions,
      quickClassTools,
      activeTeachingRole,
      getScope,
      setMode,
      setActiveStudentId,
      setActiveClassId,
      setActiveStudent,
      setActiveClass,
      setModeStudent,
      setModeClass,
      clearContext,
      setStudents,
      setStudentsLoading,
      setStudentsError,
      requestReloadStudents,
      setClasses,
      setClassesLoading,
      setClassesError,
      requestReloadClasses,
      setClassName,
      setTimelineItems
    ]
  );

  return <WorkspaceContext.Provider value={value}>{props.children}</WorkspaceContext.Provider>;
}
